import AccountingConnection from "../../entities/accountingConnection";

/**
 * AccountingConnection actions
 *
 * Call order:
 *
 * ACCOUNTINGCONNECTION_SAVE_QUEUE:	adds the item to the save queue
 * --> ACCOUNTINGCONNECTION_SAVE:	modifies the state
 * --> ACCOUNTINGCONNECTION_SAVE_SUCCESS: if all went well
 * --> ACCOUNTINGCONNECTION_SAVE_FAILURE:	if either the queue or local save failed
 *
 * Note that the only way to fail ACCOUNTINGCONNECTION_SAVE_QUEUE is during the call to ACCOUNTINGCONNECTION_SAVE,
 * as adding an item to the queue should never fail.
 *
 * OWA-1018: the reducer shows a queued save at ACCOUNTINGCONNECTION_SAVE_QUEUE, marked pending, and only that
 * save's own outcome settles the mark: ACCOUNTINGCONNECTION_SAVE_SUCCESS, ACCOUNTINGCONNECTION_SAVE_REVERT (refused, or
 * never queued), a refusal the queue never retries, or ACCOUNTINGCONNECTION_PENDING_CHECK (it left the queue).
 */

export const ACCOUNTINGCONNECTION_FETCH_ONE_QUEUE: any = 'ACCOUNTINGCONNECTION_FETCH_ONE_QUEUE';
export const ACCOUNTINGCONNECTION_FETCH_SUCCESS: any = 'ACCOUNTINGCONNECTION_FETCH_SUCCESS';
export const ACCOUNTINGCONNECTION_FETCH_FAILURE: any = 'ACCOUNTINGCONNECTION_FETCH_FAILURE';
export const ACCOUNTINGCONNECTION_FETCH_ONE_SUCCESS: any = 'ACCOUNTINGCONNECTION_FETCH_ONE_SUCCESS';
export const ACCOUNTINGCONNECTION_FETCH_ONE_FAILURE: any = 'ACCOUNTINGCONNECTION_FETCH_ONE_FAILURE';

export const ACCOUNTINGCONNECTION_SAVE: any = 'ACCOUNTINGCONNECTION_SAVE';
export const ACCOUNTINGCONNECTION_SAVE_QUEUE: any = 'ACCOUNTINGCONNECTION_SAVE_QUEUE';
export const ACCOUNTINGCONNECTION_SAVE_SUCCESS: any = 'ACCOUNTINGCONNECTION_SAVE_SUCCESS';
export const ACCOUNTINGCONNECTION_SAVE_FAILURE: any = 'ACCOUNTINGCONNECTION_SAVE_FAILURE';

export const ACCOUNTINGCONNECTION_SAVE_MANY: any = 'ACCOUNTINGCONNECTION_SAVE_MANY';
export const ACCOUNTINGCONNECTION_SAVE_MANY_QUEUE: any = 'ACCOUNTINGCONNECTION_SAVE_MANY_QUEUE';
export const ACCOUNTINGCONNECTION_SAVE_MANY_SUCCESS: any = 'ACCOUNTINGCONNECTION_SAVE_MANY_SUCCESS';
export const ACCOUNTINGCONNECTION_SAVE_MANY_FAILURE: any = 'ACCOUNTINGCONNECTION_SAVE_MANY_FAILURE';

export const ACCOUNTINGCONNECTION_REMOVE: any = 'ACCOUNTINGCONNECTION_REMOVE';
export const ACCOUNTINGCONNECTION_REMOVE_QUEUE: any = 'ACCOUNTINGCONNECTION_REMOVE_QUEUE';
export const ACCOUNTINGCONNECTION_REMOVE_SUCCESS: any = 'ACCOUNTINGCONNECTION_REMOVE_SUCCESS';
export const ACCOUNTINGCONNECTION_REMOVE_FAILURE: any = 'ACCOUNTINGCONNECTION_REMOVE_FAILURE';

export const ACCOUNTINGCONNECTION_RESTORE: any = 'ACCOUNTINGCONNECTION_RESTORE';
export const ACCOUNTINGCONNECTION_RESTORE_QUEUE: any = 'ACCOUNTINGCONNECTION_RESTORE_QUEUE';
export const ACCOUNTINGCONNECTION_RESTORE_SUCCESS: any = 'ACCOUNTINGCONNECTION_RESTORE_SUCCESS';
export const ACCOUNTINGCONNECTION_RESTORE_FAILURE: any = 'ACCOUNTINGCONNECTION_RESTORE_FAILURE';

export const ACCOUNTINGCONNECTION_RESTORE_MANY: any = 'ACCOUNTINGCONNECTION_RESTORE_MANY';
export const ACCOUNTINGCONNECTION_REMOVE_MANY: any = 'ACCOUNTINGCONNECTION_REMOVE_MANY';

// OWA-1018: settle this tab's pending save of a record
export const ACCOUNTINGCONNECTION_SAVE_REVERT: any = 'ACCOUNTINGCONNECTION_SAVE_REVERT';
export const ACCOUNTINGCONNECTION_PENDING_CHECK: any = 'ACCOUNTINGCONNECTION_PENDING_CHECK';

/* Fetch actions */
export const fetchAccountingConnectionSuccess = (accountingConnections: AccountingConnection[], partial: boolean, lastFetch: number) => ({type: ACCOUNTINGCONNECTION_FETCH_SUCCESS, accountingConnections, partial, lastFetch});
export const fetchAccountingConnectionFailure = (err: any) => {
		console.error('AccountingConnectionAction: fetchAccountingConnectionFailure', err);
		return {type: ACCOUNTINGCONNECTION_FETCH_FAILURE, err};
};
export const fetchOneAccountingConnectionQueue = (accountingConnectionId: string, sentAddedOn?: number) => ({type: ACCOUNTINGCONNECTION_FETCH_ONE_QUEUE, accountingConnectionId, sentAddedOn});
export const fetchOneAccountingConnectionSuccess = (accountingConnection: AccountingConnection | null, sentAddedOn?: number, accountingConnectionId: string = accountingConnection?.id) => ({type: ACCOUNTINGCONNECTION_FETCH_ONE_SUCCESS, accountingConnectionId, accountingConnection, sentAddedOn});
export const fetchOneAccountingConnectionFailure = (id: string, err: any) => {
		console.error('AccountingConnectionAction: fetchOneAccountingConnectionFailure', err);
		return {type: ACCOUNTINGCONNECTION_FETCH_ONE_FAILURE, id, err};
};

/* Save actions */
export const saveAccountingConnection = (accountingConnection: AccountingConnection) => {
    // console.debug('⮂ ACCOUNTINGCONNECTION_SAVE action', accountingConnection?.id);
    return {type: ACCOUNTINGCONNECTION_SAVE, accountingConnection};
}
export const saveAccountingConnectionQueue = (accountingConnection: AccountingConnection, onQueued?: (saved: boolean) => void) => {
    // console.debug('⮂ ACCOUNTINGCONNECTION_SAVE_QUEUE action', accountingConnection?.id);
    return {type: ACCOUNTINGCONNECTION_SAVE_QUEUE, accountingConnection, onQueued};
}
export const saveAccountingConnectionSuccess = (accountingConnection: AccountingConnection, sentAddedOn?: number) => {
    // console.debug('⮂ ACCOUNTINGCONNECTION_SAVE_SUCCESS action', accountingConnection?.id);
    return {type: ACCOUNTINGCONNECTION_SAVE_SUCCESS, accountingConnection, sentAddedOn};
}
export const saveAccountingConnectionFailure = (accountingConnection: AccountingConnection, status: number, message: string) => {
    console.warn(`⮂ ACCOUNTINGCONNECTION_SAVE_FAILURE error saving accountingConnection: HTTP ${status}: ${message}`, accountingConnection?.id);
    return {type: ACCOUNTINGCONNECTION_SAVE_FAILURE, accountingConnection, status, message};
};

/* SaveMany actions */
export const saveManyAccountingConnections = (accountingConnections: AccountingConnection[]) => {
    // console.debug('⮂ ACCOUNTINGCONNECTION_SAVE_MANY action', accountingConnections?.map(accountingConnection => accountingConnection?.id).join(','));
    return {type: ACCOUNTINGCONNECTION_SAVE_MANY, accountingConnections};
}
export const saveManyAccountingConnectionsQueue = (accountingConnections: AccountingConnection[], onQueued?: (saved: boolean) => void) => {
    // console.debug('⮂ ACCOUNTINGCONNECTION_SAVE_MANY_QUEUE action', accountingConnections?.map(accountingConnection => accountingConnection.id).join(','));
    return {type: ACCOUNTINGCONNECTION_SAVE_MANY_QUEUE, accountingConnections, onQueued};
}

/** `sentAddedOns`: the queue key each answer acknowledges, aligned with `accountingConnections` (OWA-1018) */
export const saveManyAccountingConnectionsSuccess = (accountingConnections: AccountingConnection[], sentAddedOns?: (number | undefined)[]) => {
    // console.debug('⮂ ACCOUNTINGCONNECTION_SAVE_MANY_SUCCESS action', accountingConnections?.map(accountingConnection => accountingConnection.id).join(','));
    return {type: ACCOUNTINGCONNECTION_SAVE_MANY_SUCCESS, accountingConnections, sentAddedOns};
}
export const saveManyAccountingConnectionsFailure = (accountingConnections: AccountingConnection[], status: number, message: string) => {
		console.warn(`⮂ SAVE_MANY_ACCOUNTINGCONNECTIONS_FAILURE Error saving accountingConnections: HTTP ${status}: ${message}`, accountingConnections?.map(accountingConnection => accountingConnection.id).join(','));
		return {type: ACCOUNTINGCONNECTION_SAVE_MANY_FAILURE, accountingConnections, status, message};
};

/* Remove actions */
export const removeAccountingConnection = (accountingConnection: AccountingConnection) => ({ type: ACCOUNTINGCONNECTION_REMOVE, accountingConnection });
export const removeAccountingConnectionQueue = (accountingConnection: AccountingConnection) => ({ type: ACCOUNTINGCONNECTION_REMOVE_QUEUE, accountingConnection });
export const removeAccountingConnectionSuccess = (accountingConnection: AccountingConnection) => ({ type: ACCOUNTINGCONNECTION_REMOVE_SUCCESS, accountingConnection });
export const removeAccountingConnectionFailure = (accountingConnection: AccountingConnection, status: number, message: string) => {
	console.error(`Redux: removeAccountingConnectionFailure Error removing accountingConnection: HTTP ${status}: ${message}`);
	return ({ type: ACCOUNTINGCONNECTION_REMOVE_FAILURE, accountingConnection, status, message });
};

/* Restore actions */
export const restoreAccountingConnection = (accountingConnection: AccountingConnection) => ({ type: ACCOUNTINGCONNECTION_RESTORE, accountingConnection });
export const restoreAccountingConnectionQueue = (accountingConnection: AccountingConnection) => ({ type: ACCOUNTINGCONNECTION_RESTORE_QUEUE, accountingConnection });
export const restoreAccountingConnectionSuccess = (accountingConnection: AccountingConnection, sentAddedOn?: number) => ({ type: ACCOUNTINGCONNECTION_RESTORE_SUCCESS, accountingConnection, sentAddedOn });
export const restoreAccountingConnectionFailure = (accountingConnection: AccountingConnection, status: number, message: string) => {
	console.error(`Redux: restoreAccountingConnectionFailure Error restoring accountingConnection: HTTP ${status}: ${message}`);
	return ({ type: ACCOUNTINGCONNECTION_RESTORE_FAILURE, accountingConnection, status, message });
};

/* RestoreMany actions */
export const restoreManyAccountingConnections = (accountingConnections: AccountingConnection[]) => ({ type: ACCOUNTINGCONNECTION_RESTORE_MANY, accountingConnections });

/* RemoveMany actions */
export const removeManyAccountingConnections = (accountingConnections: AccountingConnection[]) => ({ type: ACCOUNTINGCONNECTION_REMOVE_MANY, accountingConnections });

/* Pending save actions (OWA-1018) */
/**
 * The save `accountingConnection` (queue key `sentAddedOn`, default its addedOn) will not be accepted: it was
 * refused, or never reached the queue. Its row returns to the latest accepted version, or to
 * `current` (the record as the server holds it) when that is newer; `keep` leaves the row shown.
 */
export const revertAccountingConnectionSave = (accountingConnection: AccountingConnection, options: {sentAddedOn?: number, current?: AccountingConnection | null, keep?: boolean} = {}) =>
    ({type: ACCOUNTINGCONNECTION_SAVE_REVERT, accountingConnection, ...options});
/** processQueue read the shared queue at `readAt` (performance.now()): the live and the permanently refused queue keys */
export const checkAccountingConnectionPendingSaves = (live: number[], refused: number[], readAt: number) =>
    ({type: ACCOUNTINGCONNECTION_PENDING_CHECK, live, refused, readAt});